import { useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadSimple, FilePdf } from "@phosphor-icons/react";

function useUpload(onBusy) {
  const [busy, setBusy] = useState(false);
  const run = async (files) => {
    setBusy(true);
    onBusy?.(1);
    try {
      return await Promise.all(files.map(async (f) => {
        const fd = new FormData();
        fd.append("file", f);
        return (await api.post("/upload", fd)).data;
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      return [];
    } finally { setBusy(false); onBusy?.(-1); }
  };
  return [busy, run];
}

function Picker({ label, accept, multiple, busy, onFiles, testid }) {
  return (
    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-widest cursor-pointer hover:bg-[#D4AF37]/10">
      <UploadSimple size={14} /> {busy ? "Uploading…" : label}
      <input type="file" hidden accept={accept} multiple={multiple} disabled={busy} data-testid={testid}
        onChange={e => { const f = [...e.target.files]; e.target.value = ""; if (f.length) onFiles(f); }} />
    </label>
  );
}

const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-slate-400 block mb-1">{children}</label>;

export function CoverUpload({ value, onChange, onBusy }) {
  const [busy, run] = useUpload(onBusy);
  return (
    <div>
      <Label>Cover Photo</Label>
      <div className="flex items-center gap-3">
        {value?.path && (
          <div className="relative w-24 h-16 rounded-md overflow-hidden border border-[#1E293B]">
            <img src={fileUrl(value.path)} alt="Cover" className="w-full h-full object-cover" data-testid="cover-preview" />
            <button type="button" onClick={() => onChange(null)} className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white"><X size={10} /></button>
          </div>
        )}
        <Picker label={value?.path ? "Replace" : "Upload"} accept="image/*" busy={busy} testid="cover-upload-input"
          onFiles={async (f) => { const [r] = await run(f); if (r) onChange(r); }} />
      </div>
    </div>
  );
}

export function GalleryUpload({ value, onChange, onBusy }) {
  const [busy, run] = useUpload(onBusy);
  const list = value || [];
  return (
    <div>
      <Label>Project Photo Gallery</Label>
      <div className="flex flex-wrap items-center gap-2">
        {list.map((g, i) => (
          <div key={g.path} className="relative w-20 h-14 rounded-md overflow-hidden border border-[#1E293B]">
            <img src={fileUrl(g.path)} alt={g.name} className="w-full h-full object-cover" data-testid="gallery-preview" />
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white"><X size={10} /></button>
          </div>
        ))}
        <Picker label="Add Photos" accept="image/*" multiple busy={busy} testid="gallery-upload-input"
          onFiles={async (f) => { const r = await run(f); if (r.length) onChange([...list, ...r]); }} />
      </div>
    </div>
  );
}

export function DocUpload({ label, value, onChange, accept = "application/pdf,image/*", testid, onBusy }) {
  const [busy, run] = useUpload(onBusy);
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-3 flex-wrap">
        {value?.path && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-300">
            <FilePdf size={14} className="text-[#D4AF37]" />
            <a href={fileUrl(value.path)} target="_blank" rel="noreferrer" className="hover:underline max-w-[140px] truncate">{value.name}</a>
            <button type="button" onClick={() => onChange(null)} className="text-slate-500 hover:text-white"><X size={12} /></button>
          </span>
        )}
        <Picker label={value?.path ? "Replace" : "Upload"} accept={accept} busy={busy} testid={testid}
          onFiles={async (f) => { const [r] = await run(f); if (r) onChange(r); }} />
      </div>
    </div>
  );
}
